import User from "./user";
import Parameter from "./parameter";
import Msg from "./message";
import List from "./list";
import Field from "./field";
import Entity from "./entity";
import CollectionState from "./collection-state";
import CollectionFilters from "./collection-filters";
import { arrayRemove, clone, equal, sortBy, uniq } from "../utils";
import IListViewState from "../interface/list-view-state";
import IListView from "../interface/list-view";
import IList from "../interface/list";
import IFieldWidth from "../interface/field-width";
import IFieldSorting from "../interface/field-sorting";
import IField from "../interface/field";
import IEntityFilter from "../interface/entity-filter";
import IEntity from "../interface/entity";
import Global from "../global";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class ListViewInstance {

    /* Public Methods */

    public getAll(entityId: number | null = null): IListView[] {
        if (entityId) {
            return this.getStore().getAll().filter((view: IListView) => {
                return view.entityId === entityId && !view.isGlobal;
            });
        }
        return this.getStore().getAll();
    }

    public get(id: number): IListView | null {
        return this.getStore().get(id);
    }

    public async create(view: IListView, listId: number): Promise<IListView> {
        const isDefault: boolean = view.isDefault || false;
        delete view.isDefault;
        const list: IList | null = List.get(listId);
        if (list) {
            const entity: IEntity | null = Entity.get(list.entityId);
            view.name = (view.name || '').trim();
            if (entity && view.name) {
                const global: IListView = await this.getEntityGlobal(entity.id) as IListView;
                view.state = global.state;
                view.isGlobal = false;
                view.entityId = entity.id;
                view.icon = view.icon || Global.DEFAULT_TABLE_VIEW_ICON;
                const result: IListView = await this.getStore().create(view);
                await List.addView(listId, result.id as number, isDefault);
                return this.get(result.id as number) as IListView;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async update(view: IListView, listId: number): Promise<IListView> {
        const current: IListView | null = this.get(view.id as number);
        if (current) {
            current.name = view.name.trim();
            current.icon = view.icon;
            if (typeof view.isDefault === 'boolean') {
                await List.setDefaultView(listId, current.id as number, view.isDefault);
            }
            return await this.updateWithState(current);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number): Promise<number> {
        await List.excludeView(id);
        return this.getStore().delete(id);
    }

    public async clone(viewId: number, listId: number, rename: boolean = true): Promise<IListView | null> {
        const view: IListView | null = this.get(viewId);
        if (view) {
            const cl: IListView = clone(view);
            delete cl.id;
            if (rename) {
                cl.name = `${Msg.get(Message.copy)} ${cl.name}`;
            }
            const result: IListView = await this.getStore().create(cl);
            const list: IList | null = List.get(listId);
            if (list) {
                list.views.push(result.id as number);
                await List.update(list);
            }
            return result;
        }
        return null;
    }

    public async getByEntityId(entityId: number): Promise<IListView[]> {
        const view: IListView | null = await this.getEntityGlobal(entityId);
        if (view) {
            view.isDefault = true;
            return [view];
        }
        return [];
    }

    public async getByListId(listId: number): Promise<IListView[]> {
        const list: IList | null = List.get(listId);
        if (list) {
            const result: IListView[] = [];
            if (list.defaultViewId) {
                const view: IListView | null = ListView.get(list.defaultViewId);
                if (!view) {
                    list.defaultViewId = null;
                }
            }
            if (list.useGlobal) {
                result.push(await ListView.getEntityGlobal(list.entityId) as IListView);
            }
            list.views = uniq(list.views);
            list.views.forEach((id: number) => {
                const view: IListView | null = ListView.get(id);
                if (view) {
                    view.isDefault = view.id === list.defaultViewId;
                    result.push(view);
                }
            });
            if (!list.defaultViewId && result.length) {
                result[0].isDefault = true;
            }
            return result;
        }
        return [];
    }

    public async getByRelationId(relationId: number): Promise<IListView[]> {
        const list: IList | null = await List.getByRelation(relationId);
        if (list) {
            return await this.getByListId(list.id as number);
        }
        return [];
    }

    public async saveState(viewId: number, state: IListViewState): Promise<IListView> {
        const view: IListView | null = this.get(viewId);
        if (view && User.hasDesignerAccess()) {
            let changed: boolean = false;
            /* Check if "filters" changed */
            if (!equal(view.state.filters, state.filters)) {
                view.state.filters = state.filters;
                changed = true;
            }
            /* Check if "sorting" changed */
            if (!equal(view.state.sorting, state.sorting)) {
                view.state.sorting = state.sorting;
                changed = true;
            }
            /* Check if "fieldsOrder" changed */
            const orderOld: number[] = CollectionState.validateFields(view.entityId, view.state.fieldsOrder);
            const orderNew: number[] = CollectionState.validateFields(view.entityId, state.fieldsOrder);
            if (!equal(orderOld, orderNew)) {
                view.state.fieldsOrder = orderNew;
                changed = true;
            }
            /* Check if "fieldsWidths" changed */
            if (!equal(view.state.fieldsWidths, state.fieldsWidths)) {
                view.state.fieldsWidths = state.fieldsWidths;
                changed = true;
            }
            /* Check if "fieldsHidden" changed */
            const hiddenOld: number[] = CollectionState.validateFields(view.entityId, view.state.fieldsHidden);
            const hiddenNew: number[] = CollectionState.validateFields(view.entityId, state.fieldsHidden);
            if (!equal(hiddenOld, hiddenNew)) {
                view.state.fieldsHidden = hiddenNew;
                changed = true;
            }
            /* Check if "fieldsHidden" changed */
            const readonlyOld: number[] = CollectionState.validateFields(view.entityId, view.state.fieldsReadonly);
            const readonlyNew: number[] = CollectionState.validateFields(view.entityId, state.fieldsReadonly);
            if (!equal(readonlyOld, readonlyNew)) {
                view.state.fieldsReadonly = readonlyNew;
                changed = true;
            }
            /* Check if "fieldLocked" changed */
            if (!equal(view.state.fieldLocked, state.fieldLocked)) {
                view.state.fieldLocked = state.fieldLocked;
                changed = true;
            }
            /* Check if anything has been changed and save if it does */
            if (changed) {
                return await this.updateWithState(view);
            } else {
                return view;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async replaceFieldConfig(entityId: number, oldId: number, newId: number): Promise<void> {
        const views: IListView[] = this.getAll(entityId);
        const global: IListView | null = await this.getEntityGlobal(entityId);
        if (global) {
            views.push(global);
        }
        for (let i = 0; i < views.length; i++) {
            const view: IListView = views[i];
            let changed: boolean = false;
            /* Delete filters for old field */
            const filters: IEntityFilter[] = [];
            view.state.filters.forEach((filter: IEntityFilter) => {
                if (filter.pathId.includes(oldId)) {
                    filters.push(filter);
                }
            });
            filters.forEach((filter: IEntityFilter) => {
                arrayRemove(view.state.filters, filter);
                changed = true;
            });
            /* Replace sorting field id */
            view.state.sorting.forEach((rule: IFieldSorting) => {
                if (rule.fieldId === oldId) {
                    rule.fieldId = newId;
                    changed = true;
                }
            });
            /* Replace locked field id */
            if (view.state.fieldLocked === oldId) {
                view.state.fieldLocked = newId;
                changed = true;
            }
            /* Replace field width */
            view.state.fieldsWidths.forEach((config: IFieldWidth) => {
                if (config.id === oldId) {
                    config.id = newId;
                    changed = true;
                }
            });
            /* Replace field order */
            if (view.state.fieldsOrder.includes(oldId)) {
                view.state.fieldsOrder[view.state.fieldsOrder.indexOf(oldId)] = newId;
                changed = true;
            }
            /* Replace field hidden */
            if (view.state.fieldsHidden.includes(oldId)) {
                view.state.fieldsHidden[view.state.fieldsHidden.indexOf(oldId)] = newId;
                changed = true;
            }
            /* Save if it needs */
            if (changed) {
                await this.getStore().update(view);
            }
        }
    }

    public async deleteByEntityId(entityId: number): Promise<void> {
        const global: IListView | null = this.getEntityGlobalOrNull(entityId);
        if (global) {
            await this.delete(global.id as number);
        }
        const views: IListView[] = this.getAll(entityId);
        for (let i = 0; i < views.length; i++) {
            await this.delete(views[i].id as number);
        }
    }

    public async getEntityGlobal(entityId: number): Promise<IListView | null> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            const view: IListView | null = this.getEntityGlobalOrNull(entityId);
            if (view) {
                view.name = Msg.get(Message.globalview);
                view.icon = Global.DEFAULT_TABLE_VIEW_ICON;
                return view;
            } else {
                await this.getStore().create(this.getEmpty(entityId, true));
                return await this.getEntityGlobal(entityId);
            }
        }
        return null;
    }

    public getPrepared(id: number): IListView | null {
        const view: IListView | null = this.get(id);
        if (view) {
            return this.prepare(view);
        }
        return null;
    }

    public getEntityGlobalOrNull(entityId: number): IListView | null {
        return this.getAll().find((v: IListView) => {
            return v.isGlobal && v.entityId === entityId
        }) || null;
    }

    /* Private Methods */

    private async updateWithState(view: IListView): Promise<IListView> {
        /* Validate filters */
        view.state.filters = CollectionFilters.getValidFilters(view.state.filters);
        /* Validate sorting */
        view.state.sorting = view.state.sorting.filter((rule: IFieldSorting) => {
            const field: IField | null = Field.get(rule.fieldId);
            return !!field;
        });
        sortBy(view.state.sorting, 'order');
        view.state.sorting.forEach((rule: IFieldSorting, index: number) => {
            rule.order = index + 1;
        });
        /* Validate locked */
        if (view.state.fieldLocked) {
            const field: IField | null = Field.get(view.state.fieldLocked);
            view.state.fieldLocked = field ? view.state.fieldLocked : null;
        }
        /* Validate order */
        view.state.fieldsOrder = view.state.fieldsOrder.filter((id: number) => {
            const field: IField | null = Field.get(id);
            return !!field;
        });
        /* Validate widths */
        view.state.fieldsWidths = view.state.fieldsWidths.filter((cfg: IFieldWidth) => {
            const field: IField | null = Field.get(cfg.id);
            return !!field;
        });
        /* Validate hidden */
        view.state.fieldsHidden = view.state.fieldsHidden.filter((id: number) => {
            const field: IField | null = Field.get(id);
            return !!field;
        });
        return await this.getStore().update(view);
    }

    private prepare(view: IListView): IListView {
        const entity: IEntity | null = Entity.get(view.entityId);
        view.state.fieldsOrder = view.state.fieldsOrder.filter((id: number) => {
            const field: IField | null = Field.get(id);
            return !!field && field.entityId === view.entityId;
        });
        if (entity) {
            entity.fields.forEach((field: IField) => {
                const id: number = field.id;
                if (field.uiType !== FieldType.pointer && !view.state.fieldsOrder.includes(id) && !view.state.fieldsHidden.includes(id)) {
                    view.state.fieldsOrder.push(id);
                }
            });
        }
        return view;
    }

    private getEmpty(entityId: number, isGlobal: boolean): IListView {
        return {
            entityId,
            isGlobal,
            name: '',
            icon: '',
            state: {
                filters: [],
                sorting: [],
                fieldsHidden: [],
                fieldsReadonly: [],
                fieldsOrder: [],
                fieldsWidths: [],
                fieldLocked: null
            }
        };
    }

    private getStore(): ParameterStorage<IListView> {
        return Parameter.get(ParamStorage.Views);
    }

}

const ListView = new ListViewInstance();

export default ListView;